import {useMemo} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Modal} from "@pg-design/modal-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {Country} from "@pg-rp/common-types";

import {ApplicationBox} from "../../../application/components/ApplicationBox";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {LeadSidebar} from "../../../lead/components/lead_sidebar/LeadSidebar";
import {SidebarTestContainer} from "../../../lead/components/lead_sidebar/SidebarTestContainer";
import {OfferDisplayType} from "../../../offer/helpers/OfferDisplayType";
import {getOfferDisplayStatus} from "../../../offer/utils/get_offer_display_status";
import {PropertyListPlanModalContent} from "../../../property_list/PropertyListPlanModalContent";
import {PropertyListPlanModalHeader} from "../../../property_list/PropertyListPlanModalHeader";
import {generateHash} from "../../../utils/generate_hash";
import {IPropertyListProperty} from "../../types/PropertyListProperty";
import {IPropertyPlanModalOffer} from "../../types/PropertyPlanModalOffer";
import {PropertyPlanDisplay} from "../PropertyPlanDisplay";

interface IProps {
    isOpen: boolean;
    property: IPropertyListProperty;
    offer: IPropertyPlanModalOffer;
    pageCount: number;
    currentPropertyIndex: number;
    currentPage: number;
    propertiesOnCurrentPage: number;
    pageIsLoading: boolean;
    handleClose: () => void;
    handleNextClick: (index: number) => Promise<void>;
    handlePrevClick: (index: number) => Promise<void>;
    onPlanDownloadClick: () => void;
}

export function PropertyPlanModal(props: IProps) {
    const {
        property,
        offer,
        pageCount,
        pageIsLoading,
        currentPage,
        currentPropertyIndex,
        propertiesOnCurrentPage,
        isOpen,
        handleClose,
        handleNextClick,
        handlePrevClick,
        onPlanDownloadClick
    } = props;

    const storeHash = useMemo(() => generateHash(), []);
    const responsiveLinkTarget = useResponsiveLinkTarget();

    const isForSale = offer.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isLimited = offer.configuration.limited_presentation;
    const isAbroad = offer.region.country !== Country.POLAND;

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const isClassicView = (isForSale || (isForSaleOverLimit && isAutoresponderEnabled)) && !isLimited;
    const isPropertyForSale = property && property.for_sale;

    const buildLink = (propertyId: number) =>
        rpAppLink.property.detail.base({
            vendorSlug: offer.vendor.slug,
            offerSlug: offer.slug,
            offerId: offer.id,
            propertyId: propertyId
        });

    return (
        <Modal fullScreen isOpen={isOpen} onModalClose={handleClose} modalTheme="dark">
            <Modal.Header variant="bar" showCloseButton={false}>
                <PropertyListPlanModalHeader
                    property={property}
                    onNextClick={handleNextClick}
                    onPrevClick={handlePrevClick}
                    index={currentPropertyIndex}
                    offer={offer}
                    pageCount={pageCount}
                    currentPage={currentPage}
                    propertiesOnCurrentPage={propertiesOnCurrentPage}
                    pageIsLoading={pageIsLoading}
                    onModalClose={handleClose}
                />
            </Modal.Header>
            <Modal.Content className={css({p: 0, md: {p: 3}})}>
                <div className={modalContentStyle}>
                    <div className={mainContentStyle}>
                        {!pageIsLoading && (
                            <PropertyPlanDisplay
                                vendor={offer.vendor}
                                property={property}
                                onPlanDownloadClick={onPlanDownloadClick}
                                legacyPlan={<PropertyListPlanModalContent property={property} offer={offer} link={buildLink(property.id)} />}
                                className={css({
                                    p: 3,
                                    md: {
                                        p: 0
                                    }
                                })}
                            />
                        )}

                        {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                            <ContactBar
                                className={contactBarStyle}
                                source={ApplicationSource.Property}
                                salesOffices={offer.sales_offices}
                                vendor={offer.vendor}
                                trackVendor={{id: offer.vendor.id, name: offer.vendor.name, slug: offer.vendor.slug}}
                                trackOffer={{
                                    geo_point: {
                                        coordinates: offer.geo_point.coordinates
                                    },
                                    id: offer.id,
                                    name: offer.name,
                                    type: offer.type,
                                    region: {
                                        country: offer.region.country,
                                        full_name: offer.region.full_name,
                                        id: offer.region.id
                                    }
                                }}
                                trackProperty={property ? property : undefined}
                                backgroundColor="#20212d"
                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                offer={offer}
                                propertyId={property?.id}
                                propertyPrice={property.price}
                            />
                        )}
                    </div>

                    {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                        <div className={leadFormHolderStyle}>
                            {!isAbroad ? (
                                <SidebarTestContainer>
                                    {[
                                        () => (
                                            <ApplicationBox
                                                officeData={offer.sales_offices?.[0]}
                                                property={property}
                                                storeHash={storeHash}
                                                offerStatusStatus={offer?.status?.status}
                                                source={ApplicationSource.Property}
                                                sourceSection={ApplicationSourceSection.SIDEBAR}
                                                vendorId={offer.vendor.id}
                                                offerId={offer.id}
                                                optionalOfferPhoneData={offer}
                                                optionalVendorInfoData={offer.vendor}
                                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                                sidebarBg="dark"
                                            />
                                        ),
                                        () => (
                                            <LeadSidebar
                                                vendor={offer.vendor}
                                                offerId={offer.id}
                                                source={ApplicationSource.PreSaleOffer}
                                                sourceSection={ApplicationSourceSection.BODY}
                                                sidebarBg="dark"
                                            />
                                        )
                                    ]}
                                </SidebarTestContainer>
                            ) : (
                                <ApplicationBox
                                    officeData={offer.sales_offices?.[0]}
                                    property={property}
                                    storeHash={storeHash}
                                    offerStatusStatus={offer?.status?.status}
                                    source={ApplicationSource.Property}
                                    sourceSection={ApplicationSourceSection.SIDEBAR}
                                    vendorId={offer.vendor.id}
                                    offerId={offer.id}
                                    optionalOfferPhoneData={offer}
                                    optionalVendorInfoData={offer.vendor}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    sidebarBg="dark"
                                />
                            )}
                            <div className={asideButtonHolderStyle}>
                                <Button
                                    as="a"
                                    href={buildLink(property.id)}
                                    className={detailsButtonStyle}
                                    target={responsiveLinkTarget}
                                    variant="none_secondary"
                                >
                                    Zobacz szczegóły
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Content>
        </Modal>
    );
}

//  Styles
const modalContentStyle = css({
    display: "flex",
    flexDirection: "column",
    pb: 0,
    minHeight: "100%",
    md: {
        flexDirection: "row",
        columnGap: 3,
        pb: 3,
        overflow: "hidden"
    }
});

const mainContentStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 100%",
    minHeight: "min-content",
    md: {
        overflow: "auto"
    }
});

const contactBarStyle = css({
    position: "sticky !important", // ugly `!important` needed - probably a problem with merging emotion with panda
    bottom: 0,
    left: 0,
    p: 1,
    mt: "auto"
});

const asideButtonHolderStyle = css({
    display: "none",
    md: {
        display: "flex",
        flexGrow: 0,
        flexShrink: 0,
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        flexDirection: "column"
    }
});

const detailsButtonStyle = css({
    color: "#fff",
    mt: 1,
    md: {
        flex: "0 0 auto",
        mt: 3
    }
});

const leadFormHolderStyle = css({
    flexGrow: 0,
    flexShrink: 0,
    display: "none",
    color: "var(--colors-secondary)",
    md: {
        display: "flex",
        flexDirection: "column",
        flexBasis: "33rem",
        flexGrow: 0,
        flexShrink: 0,
        overflow: "auto"
    }
});
import {useMemo} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Modal} from "@pg-design/modal-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {Country} from "@pg-rp/common-types";

import {ApplicationBox} from "../../../application/components/ApplicationBox";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/ApplicationSource";
import {ContactBar} from "../../../atoms/contact_bar/ContactBar";
import {useResponsiveLinkTarget} from "../../../hooks/use_responsive_link_target";
import {LeadSidebar} from "../../../lead/components/lead_sidebar/LeadSidebar";
import {SidebarTestContainer} from "../../../lead/components/lead_sidebar/SidebarTestContainer";
import {OfferDisplayType} from "../../../offer/helpers/OfferDisplayType";
import {getOfferDisplayStatus} from "../../../offer/utils/get_offer_display_status";
import {PropertyListPlanModalContent} from "../../../property_list/PropertyListPlanModalContent";
import {PropertyListPlanModalHeader} from "../../../property_list/PropertyListPlanModalHeader";
import {generateHash} from "../../../utils/generate_hash";
import {IPropertyListProperty} from "../../types/PropertyListProperty";
import {IPropertyPlanModalOffer} from "../../types/PropertyPlanModalOffer";
import {PropertyPlanDisplay} from "../PropertyPlanDisplay";

interface IProps {
    isOpen: boolean;
    property: IPropertyListProperty;
    offer: IPropertyPlanModalOffer;
    pageCount: number;
    currentPropertyIndex: number;
    currentPage: number;
    propertiesOnCurrentPage: number;
    pageIsLoading: boolean;
    handleClose: () => void;
    handleNextClick: (index: number) => Promise<void>;
    handlePrevClick: (index: number) => Promise<void>;
    onPlanDownloadClick: () => void;
}

export function PropertyPlanModal(props: IProps) {
    const {
        property,
        offer,
        pageCount,
        pageIsLoading,
        currentPage,
        currentPropertyIndex,
        propertiesOnCurrentPage,
        isOpen,
        handleClose,
        handleNextClick,
        handlePrevClick,
        onPlanDownloadClick
    } = props;

    const storeHash = useMemo(() => generateHash(), []);
    const responsiveLinkTarget = useResponsiveLinkTarget();

    const isForSale = offer.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isLimited = offer.configuration.limited_presentation;
    const isAbroad = offer.region.country !== Country.POLAND;

    const isForSaleOverLimit = getOfferDisplayStatus(offer) === "limited";
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;
    const isClassicView = (isForSale || (isForSaleOverLimit && isAutoresponderEnabled)) && !isLimited;
    const isPropertyForSale = property && property.for_sale;

    const buildLink = (propertyId: number) =>
        rpAppLink.property.detail.base({
            vendorSlug: offer.vendor.slug,
            offerSlug: offer.slug,
            offerId: offer.id,
            propertyId: propertyId
        });

    return (
        <Modal fullScreen isOpen={isOpen} onModalClose={handleClose} modalTheme="dark">
            <Modal.Header variant="bar" showCloseButton={false}>
                <PropertyListPlanModalHeader
                    property={property}
                    onNextClick={handleNextClick}
                    onPrevClick={handlePrevClick}
                    index={currentPropertyIndex}
                    offer={offer}
                    pageCount={pageCount}
                    currentPage={currentPage}
                    propertiesOnCurrentPage={propertiesOnCurrentPage}
                    pageIsLoading={pageIsLoading}
                    onModalClose={handleClose}
                />
            </Modal.Header>
            <Modal.Content className={css({p: 0, md: {p: 3}})}>
                <div className={modalContentStyle}>
                    <div className={mainContentStyle}>
                        {!pageIsLoading && (
                            <PropertyPlanDisplay
                                vendor={offer.vendor}
                                property={property}
                                onPlanDownloadClick={onPlanDownloadClick}
                                legacyPlan={<PropertyListPlanModalContent property={property} offer={offer} link={buildLink(property.id)} />}
                                className={css({
                                    p: 3,
                                    md: {
                                        p: 0
                                    }
                                })}
                            />
                        )}

                        {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                            <ContactBar
                                className={contactBarStyle}
                                source={ApplicationSource.Property}
                                salesOffices={offer.sales_offices}
                                vendor={offer.vendor}
                                trackVendor={{id: offer.vendor.id, name: offer.vendor.name, slug: offer.vendor.slug}}
                                trackOffer={{
                                    geo_point: {
                                        coordinates: offer.geo_point.coordinates
                                    },
                                    id: offer.id,
                                    name: offer.name,
                                    type: offer.type,
                                    region: {
                                        country: offer.region.country,
                                        full_name: offer.region.full_name,
                                        id: offer.region.id
                                    }
                                }}
                                trackProperty={property ? property : undefined}
                                backgroundColor="#20212d"
                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                offer={offer}
                                propertyId={property?.id}
                                propertyPrice={property.price}
                            />
                        )}
                    </div>

                    {((isClassicView && isPropertyForSale) || (!property && isClassicView)) && (
                        <div className={leadFormHolderStyle}>
                            {!isAbroad ? (
                                <SidebarTestContainer>
                                    {[
                                        () => (
                                            <ApplicationBox
                                                officeData={offer.sales_offices?.[0]}
                                                property={property}
                                                storeHash={storeHash}
                                                offerStatusStatus={offer?.status?.status}
                                                source={ApplicationSource.Property}
                                                sourceSection={ApplicationSourceSection.SIDEBAR}
                                                vendorId={offer.vendor.id}
                                                offerId={offer.id}
                                                optionalOfferPhoneData={offer}
                                                optionalVendorInfoData={offer.vendor}
                                                isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                                sidebarBg="dark"
                                            />
                                        ),
                                        () => (
                                            <LeadSidebar
                                                vendor={offer.vendor}
                                                offerId={offer.id}
                                                source={ApplicationSource.PreSaleOffer}
                                                sourceSection={ApplicationSourceSection.BODY}
                                                sidebarBg="dark"
                                            />
                                        )
                                    ]}
                                </SidebarTestContainer>
                            ) : (
                                <ApplicationBox
                                    officeData={offer.sales_offices?.[0]}
                                    property={property}
                                    storeHash={storeHash}
                                    offerStatusStatus={offer?.status?.status}
                                    source={ApplicationSource.Property}
                                    sourceSection={ApplicationSourceSection.SIDEBAR}
                                    vendorId={offer.vendor.id}
                                    offerId={offer.id}
                                    optionalOfferPhoneData={offer}
                                    optionalVendorInfoData={offer.vendor}
                                    isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                                    sidebarBg="dark"
                                />
                            )}
                            <div className={asideButtonHolderStyle}>
                                <Button
                                    as="a"
                                    href={buildLink(property.id)}
                                    className={detailsButtonStyle}
                                    target={responsiveLinkTarget}
                                    variant="none_secondary"
                                >
                                    Zobacz szczegóły
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </Modal.Content>
        </Modal>
    );
}

//  Styles
const modalContentStyle = css({
    display: "flex",
    flexDirection: "column",
    pb: 0,
    minHeight: "100%",
    md: {
        flexDirection: "row",
        columnGap: 3,
        pb: 3,
        overflow: "hidden"
    }
});

const mainContentStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 100%",
    minHeight: "min-content",
    md: {
        overflow: "auto"
    }
});

const contactBarStyle = css({
    position: "sticky !important", // ugly `!important` needed - probably a problem with merging emotion with panda
    bottom: 0,
    left: 0,
    p: 1,
    mt: "auto"
});

const asideButtonHolderStyle = css({
    display: "none",
    md: {
        display: "flex",
        flexGrow: 0,
        flexShrink: 0,
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        flexDirection: "column"
    }
});

const detailsButtonStyle = css({
    color: "#fff",
    mt: 1,
    md: {
        flex: "0 0 auto",
        mt: 3
    }
});

const leadFormHolderStyle = css({
    flexGrow: 0,
    flexShrink: 0,
    display: "none",
    color: "var(--colors-secondary)",
    md: {
        display: "flex",
        flexDirection: "column",
        flexBasis: "33rem",
        flexGrow: 0,
        flexShrink: 0,
        overflow: "auto"
    }
});
